import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import GavelIcon from '@mui/icons-material/Gavel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import moment from 'moment'
import BigModal from '../../../../../components/Modals/BigModal'
import API from '../../../../../services/API'

const Section = ({ title, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: '1px solid #E2E8F0',
      overflow: 'hidden',
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <Box sx={{ color: '#239AA4', display: 'flex' }}>{icon}</Box>
      <Typography fontWeight={900} color="#242F40">
        {title}
      </Typography>
    </Stack>

    <Box sx={{ p: 2 }}>{children}</Box>
  </Paper>
)

const SelectedDecision = ({ decision, onClose }) => {
  const [actions, setActions] = useState([])
  const [loadingActions, setLoadingActions] = useState(false)
  const [failedToLoadActions, setFailedToLoadActions] = useState(false)

  useEffect(() => {
    if (!decision?._id) {
      setActions([])
      setLoadingActions(false)
      setFailedToLoadActions(false)
      return
    }

    if (!decision?.actions?.length) {
      setActions([])
      setLoadingActions(false)
      setFailedToLoadActions(false)
      return
    }

    setLoadingActions(true)

    setFailedToLoadActions(false)

    API.post(`/commandcontrol/complex-decision-actions`, {
      actionIDs: decision.actions,
    })
      .then((res) => {
        if (res.status === 200) {
          setActions(res.data)
        } else {
          setFailedToLoadActions(true)
        }
      })
      .catch(() => {
        setFailedToLoadActions(true)
      })
      .finally(() => setLoadingActions(false))
  }, [decision?._id])

  if (!decision) return null

  const chosenOption = decision.optionsConsidered?.find((x) => x.chosen)

  return (
    <BigModal open={!!decision} onClose={onClose}>
      <Box>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 5,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <GavelIcon sx={{ fontSize: 30 }} />
            </Box>

            <Stack flex={1}>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Complex Decision
              </Typography>

              <Typography variant="h5" fontWeight={900} color="white">
                {decision.decisionNumber || 'Decision Record'}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.78 }} color="white">
                Recorded{' '}
                {moment(
                  decision.createdAt || decision.decisionMakingStarted,
                ).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Stack>

            <Chip
              label={decision.status || 'Active'}
              sx={{
                borderRadius: 999,
                backgroundColor:
                  decision.status === 'Completed' ? '#DCFCE7' : '#E6F7F9',
                color: decision.status === 'Completed' ? '#166534' : '#1E7F88',
                fontWeight: 900,
              }}
            />
          </Stack>
        </Box>

        <Stack spacing={2.5}>
          <Section title="Decision Required / Problem" icon={<GavelIcon />}>
            <Typography color="#334155">{decision.problem}</Typography>
          </Section>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Section title="Summary" icon={<FactCheckIcon />}>
                  <Stack spacing={1.25}>
                    <Typography variant="caption" color="text.secondary">
                      Started
                    </Typography>
                    <Typography fontWeight={800}>
                      {moment(decision.decisionMakingStarted).format(
                        'DD/MM/YYYY HH:mm',
                      )}
                    </Typography>

                    {decision.decisionMakingEnded && (
                      <>
                        <Divider />
                        <Typography variant="caption" color="text.secondary">
                          Ended
                        </Typography>
                        <Typography fontWeight={800}>
                          {moment(decision.decisionMakingEnded).format(
                            'DD/MM/YYYY HH:mm',
                          )}
                        </Typography>
                      </>
                    )}

                    {decision.createdBy && (
                      <>
                        <Divider />
                        <Typography variant="caption" color="text.secondary">
                          Created By
                        </Typography>
                        <Typography fontWeight={800}>
                          {decision.createdBy.firstName}{' '}
                          {decision.createdBy.lastName}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Section>
              </Grid>

              <Grid item xs={12} md={6}>
                <Section title="Final Decision" icon={<CheckCircleIcon />}>
                  {decision.finalDecision ? (
                    <Stack spacing={1}>
                      <Typography fontWeight={900} color="#242F40">
                        {decision.finalDecision}
                      </Typography>
                      {decision.decisionRationale && (
                        <Typography variant="body2" color="text.secondary">
                          {decision.decisionRationale}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      No final decision recorded yet.
                    </Alert>
                  )}
                </Section>
              </Grid>
            </Grid>
          </Box>

          <Section title="Brief Facts" icon={<FactCheckIcon />}>
            <Stack spacing={1}>
              {decision.briefFacts?.length ? (
                decision.briefFacts.map((fact, index) => (
                  <Box
                    key={fact._id || index}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Typography variant="body2">{fact.fact}</Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No facts recorded.
                </Typography>
              )}
            </Stack>
          </Section>

          <Section title="Options Considered" icon={<GavelIcon />}>
            <Stack spacing={1.25}>
              {decision.optionsConsidered?.map((option, index) => (
                <Box
                  key={option._id || index}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    backgroundColor: option.chosen ? '#E6F7F9' : '#F8FAFC',
                    border: option.chosen
                      ? '1px solid rgba(35,154,164,0.32)'
                      : '1px solid #E2E8F0',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography fontWeight={900} color="#242F40">
                        {option.option}
                      </Typography>

                      {option.chosen && (
                        <Chip
                          label="Chosen"
                          size="small"
                          sx={{
                            borderRadius: 999,
                            backgroundColor: '#239AA4',
                            color: 'white',
                            fontWeight: 900,
                          }}
                        />
                      )}
                    </Stack>

                    {option.rationale && (
                      <Typography variant="body2" color="text.secondary">
                        {option.rationale}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}

              {!chosenOption && (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  No chosen option has been marked.
                </Alert>
              )}
            </Stack>
          </Section>

          <Section title="Actions" icon={<AssignmentTurnedInIcon />}>
            <Stack spacing={1.25}>
              {loadingActions && <LinearProgress />}
              {failedToLoadActions && (
                <Alert severity="error">Failed to load actions</Alert>
              )}
              {actions?.length ? (
                actions.map((action, index) => (
                  <Box
                    key={action._id || index}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      backgroundColor: action.completed ? '#DCFCE7' : '#F8FAFC',
                      border: action.completed
                        ? '1px solid #BBF7D0'
                        : '1px solid #E2E8F0',
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Typography fontWeight={900} color="#242F40">
                          {action.action}
                        </Typography>

                        <Chip
                          label={action.completed ? 'Completed' : 'Open'}
                          size="small"
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                            backgroundColor: action.completed
                              ? '#16A34A'
                              : '#FFEDD5',
                            color: action.completed ? 'white' : '#9A3412',
                          }}
                        />
                      </Stack>

                      {action.byWhen && (
                        <Typography variant="caption" color="text.secondary">
                          Due {moment(action.byWhen).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      )}

                      {action.byWhom && (
                        <Typography variant="caption" color="text.secondary">
                          Assigned to {action.byWhom.firstName}{' '}
                          {action.byWhom.lastName}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No actions recorded.
                </Typography>
              )}
            </Stack>
          </Section>

          {decision.reviewRequired && (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              This decision has been marked as requiring review.
              {decision.reviewNotes && (
                <>
                  <br />
                  {decision.reviewNotes}
                </>
              )}
            </Alert>
          )}
        </Stack>
      </Box>
    </BigModal>
  )
}

export default SelectedDecision
